'use client';

// Intake Questions CRUD Hook (Story 7.6)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { IntakeQuestionApi } from './types';

interface IntakeQuestionsResponse {
  data: IntakeQuestionApi[];
}

async function fetchIntakeQuestions(botId: string): Promise<IntakeQuestionsResponse> {
  const res = await fetch(`/api/bots/${botId}/intake-questions`);
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error?.error?.message || 'Failed to fetch intake questions');
  }
  return res.json();
}

async function createQuestion(botId: string, questionText: string): Promise<{ data: IntakeQuestionApi }> {
  const res = await fetch(`/api/bots/${botId}/intake-questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questionText }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error?.error?.message || 'Failed to create question');
  }
  return res.json();
}

async function bulkUpdateQuestions(
  botId: string,
  questions: { id: string; questionText: string; sortOrder: number }[]
): Promise<IntakeQuestionsResponse> {
  const res = await fetch(`/api/bots/${botId}/intake-questions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questions }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error?.error?.message || 'Failed to update questions');
  }
  return res.json();
}

async function deleteQuestion(botId: string, questionId: string): Promise<void> {
  const res = await fetch(`/api/bots/${botId}/intake-questions/${questionId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error?.error?.message || 'Failed to delete question');
  }
}

export function useIntakeQuestions(botId: string) {
  return useQuery({
    queryKey: ['intake-questions', botId],
    queryFn: () => fetchIntakeQuestions(botId),
    staleTime: 30 * 1000,
  });
}

export function useCreateIntakeQuestion(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (questionText: string) => createQuestion(botId, questionText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-questions', botId] });
    },
  });
}

export function useBulkUpdateIntakeQuestions(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (questions: { id: string; questionText: string; sortOrder: number }[]) =>
      bulkUpdateQuestions(botId, questions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-questions', botId] });
    },
  });
}

export function useDeleteIntakeQuestion(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (questionId: string) => deleteQuestion(botId, questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-questions', botId] });
    },
  });
}

// Update intake-related bot settings via the intake-questions PATCH endpoint.
// This supports session-based access during the creation flow (unlike useUpdateBot
// which requires full authentication via /api/bots/[botId] PATCH).
interface IntakeSettingsPayload {
  intakeEnabled?: boolean;
  intakeCompletionMessage?: string | null;
  intakePromptMessage?: string | null;
}

async function updateIntakeSettings(botId: string, payload: IntakeSettingsPayload): Promise<void> {
  const res = await fetch(`/api/bots/${botId}/intake-questions`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error?.error?.message || 'Failed to update intake settings');
  }
}

export function useUpdateIntakeSettings(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IntakeSettingsPayload) => updateIntakeSettings(botId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot', botId] });
      queryClient.invalidateQueries({ queryKey: ['bots'] });
    },
  });
}
